import { combineEpics, ofType } from 'redux-observable';
import { map, catchError, switchMap, takeUntil, mergeMap, startWith } from 'rxjs/operators';
import { createErrorStream } from 'common/store/error';
import { of } from 'rxjs';
import { addNotification, replaceNotification, removeNotification } from 'common/mdc/notifications/actions';
import {
  TYPE_PENDING, TYPE_SUCCESS, TYPE_ERROR
} from 'common/mdc/notifications/notification';

import {
  LOAD,
  CLEAR,
  PAGINATION_CHANGE,
  loadSuccess,
  loadError,
  load,
  CREATE,
  createError,
  createSuccess,
  showCreateTagDialog,
  SEARCH_TAGS_BY_LABEL, searchTagsByLabelSuccess, searchTagsByLabelError, searchTagsByLabel as searchByLabelAction,
  EDIT, editSuccess, editError,
  DELETE, deleteSuccess,
  DETACH, detachSuccess, detachError,
  ATTACH_TAG, attachTagSuccess, attachTagError, createNewTagFromAttachSuccess, showAttchTagDialog
} from './actions';
import { load as loadTagsHistory, loadNothing } from '../../device/tagsHistory/actions';
import { detachSuccessFromTagsHistory } from '../../device/tagsHistory/actions';

const loadTag = (action, state, { tagsService }) => action.pipe(
  ofType(LOAD),
  switchMap(action$ => tagsService
    .getTagsByPageNum(action$.pageNum, action$.tagsData, action$.isDelete, action$.limit)
    .pipe(
      map(loadSuccess),
      takeUntil(action.pipe(ofType(CLEAR))),
      catchError(createErrorStream(action$, loadError)),
    )),
);

const loadMoreTags = action => action.pipe(
  ofType(PAGINATION_CHANGE),
  map(load),
);

const createTag = (action, state, { tagsService }) => action.pipe(
  ofType(CREATE),
  mergeMap((action$) => {

    const pendingNotification = addNotification({
      autoHide: false,
      type: TYPE_PENDING,
      text: "Tag create is in progress."
    });
    const failureMessage = "Some error occured. Please try again.";

    return tagsService
    .create(action$.tag)
      .pipe(
        mergeMap((response) => of(
          replaceNotification(pendingNotification.notification, {
            type: TYPE_SUCCESS,
            autoHide: true,
            text: "Tag has been created successfully.",
          }),
          createSuccess(action$.tag),
          showCreateTagDialog(false),
          action$.isAttchTag ? createNewTagFromAttachSuccess(response.tagId) : load(1, [], false, action$.limit),
        )),
        startWith(pendingNotification),
        catchError(createErrorStream(
          action$,
          createError,
          () => removeNotification(pendingNotification.notification)
        )),
      );
  }),
);

const editTag = (action, state, { tagsService }) => action.pipe(
  ofType(EDIT),
  mergeMap((action$) => {

    const pendingNotification = addNotification({
      autoHide: false,
      type: TYPE_PENDING,
      text: "Tag edit is in progress."
    });
    const failureMessage = "Some error occured. Please try again.";

    return tagsService
    .edit(action$.id, action$.tag)
      .pipe(
        mergeMap((response) => of(
          replaceNotification(pendingNotification.notification, {
            type: TYPE_SUCCESS,
            autoHide: true,
            text: "Tag has been edited successfully.",
          }),
          editSuccess(action$.id, action$.tag),
          showCreateTagDialog(false),
        )),
        startWith(pendingNotification),
        catchError(createErrorStream(
          action$,
          createError,//adding error in create error, as createNewTag.js has common code of create,edit
          () => removeNotification(pendingNotification.notification)
        )),
      );
  }),
);

const searchTagsByLabel = (action, state, {tagsService}) => action.pipe(
  ofType(SEARCH_TAGS_BY_LABEL),
  switchMap(action$ => tagsService
  .searchTagsByLabel(action$.searchInfo, action$.pageNum, action$.tagsData, action$.isDelete)
  .pipe(
    map(searchTagsByLabelSuccess),
    takeUntil(action.pipe(ofType(CLEAR))),
    catchError(createErrorStream(
      action$,
      err => {
        const failureMessage = "Some unknown error occured.";
        return addNotification({
          autoHide: true,
          type: TYPE_ERROR,
          text: err.message
                ? err.message
                : failureMessage,
        });
      }
    )),
  )),
);

const deleteTag = (action, state, { tagsService }) => action.pipe(
  ofType(DELETE),
  mergeMap((action$) => {

    const pendingNotification = addNotification({
      autoHide: false,
      type: TYPE_PENDING,
      text: "Tag delete is in progress."
    });
    const failureMessage = "Some error occured. Please try again.";
    const isDelete = true;

    return tagsService
    .deleteTagById(action$.id)
      .pipe(
        mergeMap((response) => of(
          replaceNotification(pendingNotification.notification, {
            type: TYPE_SUCCESS,
            autoHide: true,
            text: "Tag has been deleted successfully.",
          }),
          deleteSuccess(action$.id),
          action$.searchText.length >= 3 ?
          searchByLabelAction(
            {search: action$.searchText, page: action$.searchTagPageNumber, limit: action$.limit},
            action$.searchTagPageNumber,
            action$.tagsData.filter(tag=>tag.tagId!==action$.id),
            isDelete
          )
          :
          load(action$.pageNum, [], isDelete, action$.limit),
        )),
        startWith(pendingNotification),
        catchError(createErrorStream(
          action$,
          err =>
             replaceNotification(pendingNotification.notification, {
              autoHide: true,
              type: TYPE_ERROR,
              text: err.message
                    ? err.message
                    : failureMessage,
            })
        )),
      );
  }),
);

const detachTag = (action, state, { tagsService }) =>
  action.pipe(
    ofType(DETACH),
    mergeMap((action$) => {
      const pendingNotification = addNotification({
        autoHide: false,
        type: TYPE_PENDING,
        text: "Tag detach is in progress.",
      });
      const failureMessage = "Some error occured. Please try again.";

      return tagsService.detach(action$.detachInfo).pipe(
        mergeMap((response) =>
          of(
            replaceNotification(pendingNotification.notification, {
              type: TYPE_SUCCESS,
              autoHide: true,
              text: "Tag has been detached successfully.",
            }),
            detachSuccess(action$.detachInfo),
            action$.isMeterTagHistory ? detachSuccessFromTagsHistory(action$.uniqueId) : loadNothing(),
          )
        ),
        startWith(pendingNotification),
        catchError(
          createErrorStream(action$, (err) =>
            replaceNotification(pendingNotification.notification, {
              autoHide: true,
              type: TYPE_ERROR,
              text: err.message ? err.message : failureMessage,
            })
          )
        )
      );
    })
  );


const attachTag = (action, state, { tagsService }) => action.pipe(
  ofType(ATTACH_TAG),
  mergeMap((action$) => {

    const pendingNotification = addNotification({
      autoHide: false,
      type: TYPE_PENDING,
      text: "Tag attach is in progress."
    });
    const failureMessage = "Some error occured.";

    return tagsService
    .attachTag(action$.attachTagInfo)
      .pipe(
        mergeMap((response) => of(
          replaceNotification(pendingNotification.notification, {
            type: TYPE_SUCCESS,
            autoHide: true,
            text: "Tag has been attached successfully.",
          }),
          attachTagSuccess(response, action$.tagDets),
          showAttchTagDialog(false),
        )),
        startWith(pendingNotification),
        catchError(createErrorStream(
          action$,
          attachTagError,
          () => removeNotification(pendingNotification.notification)
        )),
      );
  }),
);


export default combineEpics(
  loadTag,
  loadMoreTags,
  createTag,
  searchTagsByLabel,
  editTag,
  deleteTag,
  detachTag,
  attachTag,
);
